import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, Printer, PenTool, Upload, CheckCircle, Loader2 } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';

export default function AgreementViewPage() {
  const { agreementId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isInvestor = user?.role === 'investor';
  const sigRef = useRef(null);
  const fileRef = useRef(null);
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [showSignPad, setShowSignPad] = useState(false);

  useEffect(() => {
    api.get(`/agreements/${agreementId}`).then(res => setAgreement(res.data)).catch(err => toast.error(formatError(err.response?.data?.detail))).finally(() => setLoading(false));
  }, [agreementId]);

  const handleDownload = async () => {
    try {
      const res = await api.get(`/agreements/${agreementId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${agreement?.agreement_number || 'agreement'}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handlePrint = async () => {
    try {
      const res = await api.get(`/agreements/${agreementId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      // Open in a new tab — user can then use the browser's built-in Print
      // (auto-triggering print on cross-origin blob PDF is blocked by browsers)
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (!w) {
        toast.error('Pop-up blocked. Please allow pop-ups for this site to print.');
        return;
      }
      // Try to invoke print after PDF loads (works in some browsers; harmless otherwise)
      setTimeout(() => { try { w.print(); } catch { /* fallback to manual Ctrl+P */ } }, 1000);
    } catch (err) {
      console.error('Print error:', err);
      toast.error('Print failed. Please try Download instead.');
    }
  };


  const handleSignDraw = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error('Please draw your signature'); return; }
    setSigning(true);
    try {
      const sigData = sigRef.current.toDataURL('image/png');
      await api.post(`/agreements/${agreementId}/sign`, { signature_data: sigData });
      toast.success('Agreement signed successfully!');
      setShowSignPad(false);
      const res = await api.get(`/agreements/${agreementId}`);
      setAgreement(res.data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSigning(false); }
  };

  const handleSignUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSigning(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        await api.post(`/agreements/${agreementId}/sign`, { signature_data: reader.result });
        toast.success('Agreement signed successfully!');
        const res = await api.get(`/agreements/${agreementId}`);
        setAgreement(res.data);
      };
      reader.readAsDataURL(file);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSigning(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full" /></div>;
  if (!agreement) return <div className="text-center py-12 text-brand-text-secondary">Agreement not found</div>;

  const isSigned = agreement.status === 'signed';

  return (
    <div className="space-y-6" data-testid="agreement-view-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9"><ArrowLeft size={18} /></Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold text-brand-text-primary">{agreement.agreement_number}</h1>
          <p className="text-sm text-brand-text-secondary">{agreement.client_name} — {agreement.agreement_date}</p>
        </div>
        <Badge className={`text-xs border-0 px-3 py-1 ${isSigned ? 'bg-brand-success/15 text-brand-success' : 'bg-amber-100 text-amber-700'}`}>
          {isSigned ? 'Signed' : 'Pending'}
        </Badge>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={handleDownload} variant="outline" className="rounded-lg text-sm" data-testid="agr-download-btn"><Download size={15} className="mr-1.5" /> Download PDF</Button>
        <Button onClick={handlePrint} variant="outline" className="rounded-lg text-sm" data-testid="agr-print-btn"><Printer size={15} className="mr-1.5" /> Print</Button>
        {!isSigned && (
          <Button onClick={() => setShowSignPad(true)} className="bg-brand-primary text-white rounded-lg text-sm" data-testid="agr-sign-btn">
            <PenTool size={15} className="mr-1.5" /> Sign Agreement
          </Button>
        )}
      </div>

      {/* Agreement Preview */}
      <Card className="border border-brand-border bg-white max-w-3xl mx-auto">
        <CardContent className="p-8">
          {/* Header */}
          <div className="text-center mb-6 pb-4 border-b border-brand-border">
            <h2 className="font-heading text-2xl font-bold text-brand-primary">{agreement.company_name}</h2>
            <p className="text-[10px] text-brand-text-secondary mt-0.5 font-mono">CIN: {agreement.company_cin || 'U62099WR2026PTC292718'}</p>
            <p className="text-xs text-brand-text-secondary">{agreement.company_address} | {agreement.support_email || 'info@quantora.co.in'}</p>
          </div>

          <h3 className="font-heading text-xl font-bold text-brand-text-primary text-center mb-1">CAPITAL PARTNERSHIP AGREEMENT</h3>
          <p className="text-xs text-brand-text-secondary text-center mb-6">Agreement No: {agreement.agreement_number} | Date: {agreement.agreement_date}</p>

          {/* Client Details */}
          <div className="bg-brand-bg/60 rounded-lg p-5 mb-6 border border-brand-border/50">
            <h4 className="text-xs uppercase tracking-wider font-bold text-brand-text-secondary mb-3">Client Details</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <p><span className="text-brand-text-secondary">Name:</span> <span className="font-medium text-brand-text-primary">{agreement.client_name}</span></p>
              <p><span className="text-brand-text-secondary">Client ID:</span> <span className="font-mono font-medium text-brand-primary">{agreement.client_client_id}</span></p>
              <p><span className="text-brand-text-secondary">Email:</span> <span className="text-brand-text-primary">{agreement.client_email}</span></p>
              <p><span className="text-brand-text-secondary">Branch:</span> <span className="text-brand-text-primary">{agreement.branch_name || '-'}</span></p>
            </div>
          </div>

          {/* Investment Details */}
          <div className="mb-6">
            <h4 className="text-xs uppercase tracking-wider font-bold text-brand-text-secondary mb-3">Investment Details</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-brand-bg/40 rounded-lg p-3 border border-brand-border/30">
                <p className="text-xs text-brand-text-secondary">Investment Amount</p>
                <p className="font-heading text-xl font-bold text-brand-text-primary">${Number(agreement.investment_amount || 0).toLocaleString()}</p>
              </div>
              <div className="bg-brand-bg/40 rounded-lg p-3 border border-brand-border/30">
                <p className="text-xs text-brand-text-secondary">Profit Sharing</p>
                <p className="text-sm font-medium text-brand-text-primary mt-1">
                  Investor 50% · Company 50%
                </p>
              </div>
            </div>
          </div>

          {/* Terms */}
          <div className="mb-6">
            <h4 className="text-xs uppercase tracking-wider font-bold text-brand-text-secondary mb-3">Terms & Conditions</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-brand-text-primary leading-relaxed">
              {agreement.terms?.map((term, i) => <li key={i}>{term}</li>)}
            </ol>
          </div>

          {/* Risk Disclaimer */}
          <div className="bg-red-50 rounded-lg p-4 mb-6 border border-red-100">
            <h4 className="text-xs uppercase tracking-wider font-bold text-brand-error mb-2">Risk Disclaimer</h4>
            <p className="text-sm text-brand-text-secondary leading-relaxed">{agreement.risk_disclaimer}</p>
          </div>

          {/* Signature */}
          <div className="border-t border-brand-border pt-6">
            <h4 className="text-xs uppercase tracking-wider font-bold text-brand-text-secondary mb-4">Signatures</h4>
            <div className="grid grid-cols-2 gap-8">
              <div>
                {agreement.signature_data && (
                  <img src={agreement.signature_data} alt="Client Signature" className="h-16 mb-2 border border-brand-border/30 rounded p-1 bg-white" />
                )}
                <div className="border-t border-brand-text-secondary/30 pt-2">
                  <p className="text-sm font-medium text-brand-text-primary">{agreement.client_name}</p>
                  <p className="text-xs text-brand-text-secondary">
                    {isSigned ? `Signed: ${agreement.signed_at?.slice(0, 10)}` : 'Pending Signature'}
                  </p>
                  {isSigned && <p className="text-[10px] text-brand-success flex items-center gap-1 mt-1"><CheckCircle size={10} /> Digitally Signed</p>}
                </div>
              </div>
              <div>
                <div className="border-t border-brand-text-secondary/30 pt-2 mt-16">
                  <p className="text-sm font-medium text-brand-text-primary">{agreement.branch_manager_name || agreement.company_name}</p>
                  <p className="text-xs text-brand-text-secondary">{agreement.branch_manager_name ? 'Branch Manager' : 'Authorized Signatory'}</p>
                  <p className="text-[10px] text-brand-text-secondary mt-0.5">{agreement.company_name}</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Signature Pad Modal */}
      {showSignPad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="signature-modal">
          <Card className="w-full max-w-lg mx-4 bg-white">
            <CardHeader><CardTitle className="font-heading text-lg">Sign Agreement</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-brand-text-secondary">Draw your signature below or upload a signature image.</p>
              <div className="border-2 border-dashed border-brand-border rounded-lg bg-white" data-testid="signature-canvas-container">
                <SignatureCanvas
                  ref={sigRef}
                  penColor="#0F172A"
                  canvasProps={{ width: 460, height: 180, className: 'w-full rounded-lg' }}
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => sigRef.current?.clear()} className="rounded-lg text-xs">Clear</Button>
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="rounded-lg text-xs">
                  <Upload size={13} className="mr-1" /> Upload Image
                </Button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleSignUpload} />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setShowSignPad(false)} className="rounded-lg">Cancel</Button>
                <Button onClick={handleSignDraw} disabled={signing} className="bg-brand-primary text-white rounded-lg" data-testid="confirm-sign-btn">
                  {signing ? <Loader2 size={16} className="animate-spin mr-2" /> : <PenTool size={16} className="mr-2" />} Confirm Signature
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
